import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import React, {
  CSSProperties,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useSearchInput from "utils/hooks/useSearchInput";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import TableOutlined from "@ant-design/icons/TableOutlined";
import { LayoutGrid, List } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import LayoutSwitch from "common/Switch/LayoutSwitch";
import localStorageService from "utils/localStorageService";
import { useBottomNavigator } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const LAYOUT_CHANGE_EVENT = "table-layout-change";

interface LayoutChangeDetail {
  storageKey: string;
  isCardsView: boolean;
}

interface TableHeaderRowProps {
  style?: CSSProperties;
  titleOrNode?: ReactNode;
  actionTrigger?: ReactNode;
  searchPlaceholder?: string;
  showLayoutChangeButton?: boolean;
  numOfItems?: number;
  searchInput?: ReactNode;
  setIsCardsView: (isCardsView: boolean) => void;
  isCardsView: boolean;
  bulkActions?: ReactNode;
  helpTooltip?: ReactNode;
  isDrawerTable?: boolean;
  sortSelect?: ReactNode;
}

const TableHeaderRow = ({
  style,
  titleOrNode,
  showLayoutChangeButton,
  numOfItems,
  searchInput,
  actionTrigger,
  isCardsView,
  setIsCardsView,
  bulkActions,
  helpTooltip,
  isDrawerTable,
  sortSelect,
}: TableHeaderRowProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const moveSort =
    useMedia([`(max-width: 750px)`], [true], false) && !isBottomNavigator;

  const renderLeftHeader = () => {
    if (typeof titleOrNode === "string")
      return (
        <Title type="secondary" level={3}>
          {titleOrNode}
        </Title>
      );
    return titleOrNode;
  };

  const getTableLayoutContainerStyle = () => {
    const elementCount = +Boolean(actionTrigger) + +Boolean(searchInput);
    let justifyContent: string;
    if (elementCount === 2) {
      justifyContent = isMobile ? "space-between" : "flex-end";
    } else {
      justifyContent = Boolean(searchInput) ? "flex-start" : "flex-end";
    }

    return {
      display: "flex",
      justifyContent,
      columnGap: 8,
    };
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: titleOrNode ? "space-between" : "flex-end",
          alignItems: "center",
          marginBottom: titleOrNode || Boolean(searchInput) ? 16 : 0,
          ...style,
        }}
      >
        <Flex>
          {renderLeftHeader()}
          {!isDrawerTable && !!numOfItems && (
            <Title level={4} type="secondary" style={{ marginLeft: 4 }}>
              ({numOfItems})
            </Title>
          )}
          {helpTooltip}
          {bulkActions}
        </Flex>
        <div style={getTableLayoutContainerStyle()}>
          {searchInput}
          {!moveSort && sortSelect}
          {showLayoutChangeButton && (
            <LayoutSwitch
              layoutIcons={[
                <LookIcon
                  key="list"
                  classic={<MenuOutlined />}
                  newLook={<List size={16} strokeWidth={1.75} />}
                />,
                <LookIcon
                  key="cards"
                  classic={<TableOutlined />}
                  newLook={<LayoutGrid size={16} strokeWidth={1.75} />}
                />,
              ]}
              selectedLayoutIndex={isCardsView ? 1 : 0}
              onClick={() => setIsCardsView(!isCardsView)}
            />
          )}
          {actionTrigger}
        </div>
      </div>
      {moveSort && (
        <div
          style={{
            marginTop: -4,
            marginBottom: 12,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {sortSelect}
        </div>
      )}
    </>
  );
};

export type useTableHeaderRowProps = Omit<
  TableHeaderRowProps,
  "setIsCardsView" | "isCardsView"
> & {
  isDrawerTable?: boolean;
  withSearch?: boolean;
  layoutChangeKey?: string;
  defaultCardsView?: boolean;
};
export const useTableHeaderRow = ({
  searchPlaceholder,
  isDrawerTable,
  withSearch,
  layoutChangeKey,
  defaultCardsView,
  ...props
}: useTableHeaderRowProps) => {
  const storageKey = layoutChangeKey && `${layoutChangeKey}_layout`;
  const { useMobileSearch, search: mobileSearch } =
    useContext(PageWrapperContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const getDefaultCardsView = () => {
    const rawStorageValue =
      storageKey && localStorageService.getValue(storageKey);
    const storageValue = rawStorageValue
      ? Boolean(Number(rawStorageValue))
      : undefined;
    return storageValue ?? defaultCardsView ?? (!!layoutChangeKey || isMobile);
  };
  const [isCardsView, setIsCardsView] = useState(getDefaultCardsView);
  useLayoutChangeListener(storageKey, setIsCardsView);
  const [searchInput, searchInputValue] = useSearchInput({
    placeholder: searchPlaceholder || t("labels.search_placeholder"),
    style: { width: "200px" },
    lowerCaseValue: true,
  });

  const setIsCardViewAndStorage = (isCardsView: boolean) => {
    setIsCardsView(isCardsView);
    if (storageKey) {
      localStorageService.setValue(storageKey, Number(isCardsView));
      window.dispatchEvent(
        new CustomEvent<LayoutChangeDetail>(LAYOUT_CHANGE_EVENT, {
          detail: { storageKey, isCardsView },
        })
      );
    }
  };

  const showSearch = withSearch && (!useMobileSearch || isDrawerTable);

  const tableHeader = (
    <TableHeaderRow
      searchPlaceholder={searchPlaceholder}
      searchInput={!useMobileSearch && searchInput}
      isCardsView={isCardsView}
      setIsCardsView={setIsCardViewAndStorage}
      isDrawerTable={isDrawerTable}
      {...props}
    />
  );

  const searchValue = showSearch
    ? searchInputValue
    : isMobile
    ? mobileSearch.value
    : "";

  return {
    isCardsView,
    tableHeader,
    searchValue,
  };
};

const useLayoutChangeListener = (
  storageKey: string | undefined,
  onChange: (isCardsView: boolean) => void
) =>
  useEffect(() => {
    if (!storageKey) return;
    const listener = (event: Event) => {
      const { detail } = event as CustomEvent<LayoutChangeDetail>;
      if (detail.storageKey === storageKey) onChange(detail.isCardsView);
    };
    window.addEventListener(LAYOUT_CHANGE_EVENT, listener);
    return () => window.removeEventListener(LAYOUT_CHANGE_EVENT, listener);
  }, [storageKey, onChange]);
